import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "../../GlobalFiles/Sidebar";
import { API_BASE_URL } from "../../../../../api";

const notify = (text, options) => {
  const opts = typeof options === "object" ? options : {};
  const toastId = opts.toastId || opts.id || text;
  toast(text, { toastId, ...opts });
};

const AdminActivities = () => {
  const { data } = useSelector((store) => store.auth);
  const isAdmin = data?.user?.userType === "admin";

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const [filters, setFilters] = useState({ actorType: "", targetType: "", action: "" });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const pageSize = 50;

  const headers = useMemo(() => ({}), []);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/activities`, {
        headers,
        params: {
          page,
          limit: pageSize,
          ...filters,
        },
      });
      setActivities(response.data?.data || []);
      setTotalPages(response.data?.totalPages || 1);
      setTotalActivities(response.data?.total || 0);
    } catch (error) {
      notify(error?.response?.data?.message || "Failed to load activities");
    } finally {
      setLoading(false);
    }
  }, [filters, headers, page, pageSize]);

  useEffect(() => {
    if (isAdmin) {
      loadActivities();
    }
  }, [isAdmin, loadActivities]);

  if (data?.isAuthenticated === false) {
    return <Navigate to="/" />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  const deleteActivity = async (activityId) => {
    try {
      await axios.delete(`${API_BASE_URL}/admin/activities/${activityId}`);
      notify("Activity deleted");
      await loadActivities();
    } catch (error) {
      notify(error?.response?.data?.message || "Failed to delete activity");
    }
  };

  const activityStart = totalActivities === 0 ? 0 : (page - 1) * pageSize + 1;
  const activityEnd = Math.min(page * pageSize, totalActivities);

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar onCollapse={setSidebarCollapsed} />
        <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? "ml-20" : "ml-0"}`}>
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Activity Log</h1>
                  <p className="text-gray-500 mt-1">All admin-accessible activities with filters and pagination.</p>
                </div>
                <button
                  onClick={loadActivities}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <select
                  value={filters.actorType}
                  onChange={(event) => {
                    setFilters((prev) => ({ ...prev, actorType: event.target.value }));
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All Actors</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="doctor">Doctor</option>
                  <option value="agent">Agent</option>
                  <option value="patient">Patient</option>
                  <option value="super-admin">Super Admin</option>
                  <option value="system">System</option>
                </select>
                <select
                  value={filters.targetType}
                  onChange={(event) => {
                    setFilters((prev) => ({ ...prev, targetType: event.target.value }));
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All Targets</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="doctor">Doctor</option>
                  <option value="agent">Agent</option>
                  <option value="patient">Patient</option>
                  <option value="appointment">Appointment</option>
                  <option value="report">Report</option>
                  <option value="test-order">Test Order</option>
                  <option value="test">Test</option>
                  <option value="test-category">Test Category</option>
                  <option value="doctor-payment">Doctor Payment</option>
                  <option value="agent-payment">Agent Payment</option>
                  <option value="text-info">Text Info</option>
                  <option value="sync">Sync</option>
                </select>
                <select
                  value={filters.action}
                  onChange={(event) => {
                    setFilters((prev) => ({ ...prev, action: event.target.value }));
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All Actions</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                  <option value="password-change">Password Change</option>
                </select>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-gray-900">Activities</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    Showing {activityStart}-{activityEnd} of {totalActivities}
                  </span>
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    className="px-3 py-1 rounded border border-gray-300 text-gray-700"
                    disabled={page === 1}
                  >
                    Prev
                  </button>
                  <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    className="px-3 py-1 rounded border border-gray-300 text-gray-700"
                    disabled={page >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
              {activities.length === 0 ? (
                <div className="text-center text-gray-500">No activities found.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activities.map((activity) => {
                    let icon = (
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    );
                    let bgColor = "bg-gray-100";
                    let iconColor = "text-gray-600";
                    let actionText = activity.action;

                    if (activity.action === "create") {
                      icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>;
                      bgColor = "bg-green-100";
                      iconColor = "text-green-600";
                      actionText = "created a new";
                    } else if (activity.action === "update") {
                      icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>;
                      bgColor = "bg-blue-100";
                      iconColor = "text-blue-600";
                      actionText = "updated the";
                    } else if (activity.action === "delete") {
                      icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>;
                      bgColor = "bg-red-100";
                      iconColor = "text-red-600";
                      actionText = "deleted a";
                    } else if (activity.action === "password-change") {
                      icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path></svg>;
                      bgColor = "bg-purple-100";
                      iconColor = "text-purple-600";
                      actionText = "changed password for";
                    }

                    const getTargetIdentifier = (act) => {
                      const c = act.changes || {};
                      const possibleIds = [c.patientID, c.docID, c.agentID, c.adminID, c.managerID, c.testOrderID, c.reportID, c.categoryId, c.testId];
                      const foundId = possibleIds.find(val => val !== undefined && val !== null && val !== "");

                      const possibleNames = [c.patientName, c.docName, c.name, c.adminName, c.managerName, c.title, c.categoryName];
                      const foundName = possibleNames.find(val => val !== undefined && val !== null && val !== "");

                      if (act.targetType === "test-order" || c.testOrderID) {
                        const orderNum = c.testOrderID || (foundId && !foundName ? foundId : null);
                        if (foundName && orderNum) return `${foundName} (Order #${orderNum})`;
                        if (foundName) return `${foundName}`;
                        if (orderNum) return `Order #${orderNum}`;
                      }

                      if (foundId && foundName) return `${foundName} (ID: ${foundId})`;
                      if (foundName) return foundName;
                      if (foundId) return `ID: ${foundId}`;

                      if (act.targetId) {
                        if (act.targetId.length === 24 && /^[0-9a-fA-F]{24}$/.test(act.targetId)) {
                          return `ID: #${act.targetId.slice(-6)}`;
                        }
                        return act.targetId;
                      }
                      return "N/A";
                    };

                    const targetIdent = getTargetIdentifier(activity);

                    return (
                      <div 
                        key={activity._id} 
                        className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md hover:border-blue-300 transition"
                        onClick={() => setSelectedActivity({ ...activity, icon, bgColor, iconColor, actionText, targetIdent })}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${bgColor} ${iconColor} shrink-0`}>
                              {icon}
                            </div>
                            <div className="flex flex-col">
                               <div className="flex items-center gap-2">
                                  <span className="font-bold text-gray-900 text-base">{activity.actorName || "System"}</span>
                                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 uppercase tracking-wider">{activity.actorType}</span>
                               </div>
                               <time className="text-xs text-gray-500 font-medium mt-0.5">
                                 {new Date(activity.createdAt).toLocaleDateString()} {new Date(activity.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                               </time>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-3 bg-gray-50 rounded p-2.5 border border-gray-100">
                          <p className="text-gray-700 text-sm flex flex-wrap items-center gap-1.5">
                            <span className="text-gray-600">{actionText}</span>
                            <span className="font-bold text-gray-900 capitalize">{activity.targetType.replace(/-/g, " ")}</span>
                            <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200 text-gray-800 shadow-sm">{targetIdent}</span>
                          </p>
                        </div>
                        
                        {activity.message && activity.message !== `${activity.targetType} ${activity.action}d` && (
                          <p className="text-xs text-gray-500 italic mt-3 truncate border-l-2 border-gray-200 pl-2">&quot;{activity.message}&quot;</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Details Modal */}
      {selectedActivity && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className={`flex justify-between items-center p-4 border-b ${selectedActivity.bgColor}`}>
               <div className="flex items-center">
                 <div className={`w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 ${selectedActivity.iconColor}`}>
                   {selectedActivity.icon}
                 </div>
                 <h3 className="text-lg font-bold text-gray-900 capitalize">{selectedActivity.actionText} {selectedActivity.targetType}</h3>
               </div>
               <button onClick={() => setSelectedActivity(null)} className="text-gray-500 hover:text-gray-800 bg-white/50 hover:bg-white rounded-full p-1 transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
               </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div>
                  <p className="text-gray-500 mb-1 font-medium">Actor</p>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    {selectedActivity.actorName || "System"}
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 uppercase tracking-wider">{selectedActivity.actorType}</span>
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1 font-medium">Target</p>
                  <p className="font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded inline-block border border-gray-200">{selectedActivity.targetIdent || selectedActivity.targetId}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1 font-medium">Time</p>
                  <p className="text-gray-900">{new Date(selectedActivity.createdAt).toLocaleString()}</p>
                </div>
                {selectedActivity.message && selectedActivity.message !== `${selectedActivity.targetType} ${selectedActivity.action}d` && (
                  <div className="col-span-2">
                    <p className="text-gray-500 mb-1 font-medium">Message</p>
                    <p className="text-gray-700 italic border-l-2 border-gray-300 pl-2">{selectedActivity.message}</p>
                  </div>
                )}
              </div>

              {/* Detailed changes section commented out for now
              {selectedActivity.changes && Object.keys(selectedActivity.changes).length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-3 border-b pb-2">Detailed Changes</h4>
                  
                  {selectedActivity.changes.updatedFields && Array.isArray(selectedActivity.changes.updatedFields) ? (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Modified Fields:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedActivity.changes.updatedFields.map((field, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs font-medium text-gray-700">
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded border border-gray-200 p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                        {Object.entries(selectedActivity.changes).map(([key, value]) => (
                          <div key={key} className="flex flex-col text-sm border-l-2 border-blue-200 pl-3">
                            <span className="font-medium text-gray-500 text-xs uppercase tracking-wider mb-1">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                            <span className="text-gray-900 font-semibold break-words">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              */}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button onClick={() => setSelectedActivity(null)} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminActivities;
